import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  Label,
  Textarea,
  Spinner,
  MessageBar,
  makeStyles,
  tokens,
  Divider
} from '@fluentui/react-components';
import { 
  AddRegular, 
  HeartRegular,
  PersonRegular,
  EditRegular,
  SendRegular
} from '@fluentui/react-icons';
import { addPrayer } from '../services/sharePointService';

const useStyles = makeStyles({
  // Modal Container
  modal: {
    width: '800px',
    maxWidth: '95vw',
    '@media (max-width: 768px)': {
      width: '98vw',
      maxWidth: '98vw',
    }
  },
  
  // Form Container
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
  },
  
  // Individual Form Sections
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  
  // Labels
  label: {
    fontSize: '16px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  
  // Required Badge
  requiredBadge: {
    fontSize: '12px',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
    fontWeight: tokens.fontWeightMedium,
  },
  
  // Input Fields
  input: {
    width: '100%',
    borderRadius: '4px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    transition: 'all 0.2s ease',
    fontSize: '16px',
    padding: '12px 16px',
    '&:focus': {
      border: `1px solid ${tokens.colorBrandStroke1}`,
      boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
    },
  },
  
  textarea: {
    minHeight: '120px',
    resize: 'vertical',
    borderRadius: '4px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    transition: 'all 0.2s ease',
    fontSize: '16px',
    padding: '12px 16px',
    width: '100%',
    '&:focus': {
      border: `1px solid ${tokens.colorBrandStroke1}`,
      boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
    },
  },
  
  // Character Count
  characterCount: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'right',
    marginTop: '4px',
  },
  
  // Message Bars
  messageBar: {
    borderRadius: '4px',
    marginBottom: '16px',
    width: '100%',
  },
  
  // Loading Overlay
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    zIndex: 10,
  },
  
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '20px',
  },

  // Trigger Button
  triggerButton: {
    background: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: '4px',
    padding: '8px 16px',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '14px',
    transition: 'all 0.2s ease',
    border: 'none',
    '&:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
    '&:active': {
      backgroundColor: tokens.colorBrandBackgroundPressed,
    }
  },
});

// Form Field Component
interface FormFieldProps {
  label: string;
  icon: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  characterCount?: number;
  maxCharacters?: number | undefined;
}

const FormField: React.FC<FormFieldProps> = ({ 
  label, 
  icon, 
  required = false, 
  children, 
  characterCount,
  maxCharacters 
}) => {
  const styles = useStyles();
  
  return (
    <div className={styles.formSection}>
      <Label className={styles.label}>
        {icon}
        {label}
        {required && <span className={styles.requiredBadge}>Required</span>}
      </Label>
      {children}
      {characterCount !== undefined && maxCharacters !== undefined && (
        <div className={styles.characterCount}>
          {characterCount}/{maxCharacters} characters
        </div>
      )}
    </div>
  );
};

// Message Display Component
interface MessageDisplayProps {
  error: string | null;
  success: boolean;
}

const MessageDisplay: React.FC<MessageDisplayProps> = ({ error, success }) => {
  const styles = useStyles();
  
  if (error) {
    return (
      <MessageBar intent="error" className={styles.messageBar}>
        {error}
      </MessageBar>
    );
  }
  
  if (success) {
    return (
      <MessageBar intent="success" className={styles.messageBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HeartRegular className={styles.successIcon} />
          Prayer submitted successfully! Thank you for sharing your request.
        </div>
      </MessageBar>
    );
  }
  
  return null;
};

// Main Modal Component
interface PrayerSubmissionModalProps {
  onPrayerSubmitted?: () => void;
}

export const PrayerSubmissionModal: React.FC<PrayerSubmissionModalProps> = ({ 
  onPrayerSubmitted 
}) => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    prayerText: '',
    assignedTo: ''
  });

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.prayerText.trim()) {
      setError('Please fill in both the title and prayer text.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await addPrayer({
        title: formData.title.trim(),
        prayerText: formData.prayerText.trim(),
        assignedTo: formData.assignedTo.trim()
      });

      setSuccess(true);
      setFormData({ title: '', prayerText: '', assignedTo: '' });
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        setOpen(false);
        setSuccess(false);
        onPrayerSubmitted?.();
      }, 2000);

    } catch (error) {
      console.error('Error submitting prayer:', error);
      setError('Failed to submit prayer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setFormData({ title: '', prayerText: '', assignedTo: '' });
    setError(null);
    setSuccess(false);
  };

  const canSubmit = Boolean(formData.title.trim() && formData.prayerText.trim());

  return (
    <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <DialogTrigger disableButtonEnhancement>
        <Button 
          appearance="primary" 
          icon={<AddRegular />}
          className={styles.triggerButton}
        >
          Submit a Prayer
        </Button>
      </DialogTrigger>
      
      <DialogSurface className={styles.modal}>
        <DialogBody style={{ position: 'relative' }}>
          {loading && (
            <div className={styles.loadingOverlay}>
              <Spinner size="large" />
            </div>
          )}
          
          <DialogTitle>Submit a Prayer Request</DialogTitle>
          
          <DialogContent>
            <MessageDisplay error={error} success={success} />
            
            <form className={styles.formContainer} onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              {/* Prayer Title Field */}
              <FormField
                label="Prayer Title"
                icon={<EditRegular />}
                required={true}
                characterCount={formData.title.length}
                maxCharacters={100 as number}
              >
                <Input
                  value={formData.title}
                  onChange={(e, data) => setFormData(prev => ({ ...prev, title: data.value }))}
                  placeholder="Enter a brief, descriptive title for your prayer request"
                  disabled={loading}
                  className={styles.input}
                  maxLength={100}
                />
              </FormField>
              
              <Divider />
              
              {/* Prayer Details Field */}
              <FormField
                label="Prayer Details"
                icon={<HeartRegular />}
                required={true}
                characterCount={formData.prayerText.length}
                maxCharacters={1000 as number}
              >
                <Textarea
                  value={formData.prayerText}
                  onChange={(e, data) => setFormData(prev => ({ ...prev, prayerText: data.value }))}
                  placeholder="Please share your prayer request details, concerns, or specific needs. Be as detailed as you feel comfortable sharing..."
                  className={styles.textarea}
                  disabled={loading}
                  maxLength={1000}
                />
              </FormField>
              
              <Divider />
              
              {/* Assign To Field */}
              <FormField
                label="Assign To (Optional)"
                icon={<PersonRegular />}
                required={false}
              >
                <Input
                  value={formData.assignedTo}
                  onChange={(e, data) => setFormData(prev => ({ ...prev, assignedTo: data.value }))}
                  placeholder="Enter name of person to assign this prayer to (optional)"
                  disabled={loading}
                  className={styles.input}
                />
              </FormField>
            </form>
          </DialogContent>
          
          <DialogActions>
            <Button 
              appearance="secondary" 
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              appearance="primary" 
              onClick={handleSubmit}
              disabled={loading || !canSubmit}
              icon={loading ? <Spinner size="tiny" /> : <SendRegular />}
            >
              {loading ? 'Submitting...' : 'Submit Prayer'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}; 